import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, Download } from "lucide-react";
import api from "../lib/apiClient";
import { ProductCard } from "../components/ProductCard";

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brandList, setBrandList] = useState([]);
    const [settings, setSettings] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const filters = {
        category: searchParams.get("category") || "",
        brand: searchParams.get("brand") || "",
        origin: searchParams.get("origin") || "",
        packaging: searchParams.get("packaging") || "",
        product_type: searchParams.get("product_type") || "",
        limited_time: searchParams.get("limited_time") || "",
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/brands").then((r) => setBrandList(r.data));
        api.get("/settings").then((r) => setSettings(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get("/products").then((r) => {
            setProducts(r.data);
            setLoading(false);
        });
    }, []);

    const options = useMemo(() => ({
        brands: uniq([...brandList.map((b) => b.name), ...products.map((p) => p.brand)]).sort(),
        origins: uniq(products.map((p) => p.origin)).sort(),
        packaging: uniq(products.map((p) => p.packaging)).sort(),
        types: uniq(products.map((p) => p.product_type)).sort(),
    }), [products, brandList]);

    const filtered = useMemo(() => products.filter((p) => {
        if (filters.category && p.category !== filters.category) return false;
        if (filters.brand && p.brand !== filters.brand) return false;
        if (filters.origin && p.origin !== filters.origin) return false;
        if (filters.packaging && p.packaging !== filters.packaging) return false;
        if (filters.product_type && p.product_type !== filters.product_type) return false;
        if (filters.limited_time === "true" && !p.limited_time) return false;
        if (search.trim()) {
            const q = search.toLowerCase();
            const hay = `${p.name} ${p.brand} ${p.category} ${(p.flavors || []).join(" ")}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    }), [products, searchParams, search]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value);
        else next.delete(key);
        setSearchParams(next);
    };

    const clearAll = () => setSearchParams({});
    const activeCount = Object.values(filters).filter(Boolean).length;

    const FilterGroup = ({ label, name, opts }) => (
        <div className="mb-6">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{label}</h4>
            <div className="flex flex-wrap gap-2">
                {opts.map((o) => (
                    <button
                        key={o}
                        onClick={() => setFilter(name, filters[name] === o ? "" : o)}
                        data-testid={`filter-${name}-${String(o).toLowerCase().replace(/\s|\//g, "-")}`}
                        className={`text-xs px-3 py-1.5 border transition-colors ${
                            filters[name] === o
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border hover:border-primary"
                        }`}
                    >
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div data-testid="catalog-page">
            <div className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                    <div>
                        <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">Wholesale Catalog</p>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Products</h1>
                        <p className="text-primary-foreground/70 mt-3 max-w-xl">Browse our premium wholesale range. Select case quantities and add products to your order request.</p>
                    </div>
                    {settings?.catalog_pdf && (
                        <a href={settings.catalog_pdf} download={settings.catalog_filename || "YATTO-Catalog.pdf"} data-testid="catalog-download-pdf" className="flex items-center gap-2 bg-secondary text-secondary-foreground px-6 py-3.5 font-semibold hover:bg-background hover:text-foreground transition-colors self-start">
                            <Download size={18} /> Download Catalog
                        </a>
                    )}
                </div>
            </div>

            {/* category chips */}
            <div className="border-b border-border bg-background">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-2">
                    <button onClick={() => setFilter("category", "")} className={`text-sm px-4 py-2 border transition-colors ${!filters.category ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`} data-testid="cat-chip-all">
                        All
                    </button>
                    {categories.map((c) => (
                        <button key={c.id} onClick={() => setFilter("category", c.name)} className={`text-sm px-4 py-2 border transition-colors ${filters.category === c.name ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`} data-testid={`cat-chip-${c.id}`}>
                            {c.short || c.name}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[260px_1fr] gap-10">
                {/* Sidebar filters */}
                <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
                    <div className="lg:sticky lg:top-44">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading font-bold text-lg">Filters</h3>
                            {activeCount > 0 && (
                                <button onClick={clearAll} className="text-xs text-primary flex items-center gap-1" data-testid="clear-filters">
                                    <X size={13} /> Clear ({activeCount})
                                </button>
                            )}
                        </div>
                        <FilterGroup label="Brand" name="brand" opts={options.brands} />
                        <FilterGroup label="Country of Origin" name="origin" opts={options.origins} />
                        <FilterGroup label="Packaging" name="packaging" opts={options.packaging} />
                        <FilterGroup label="Product Type" name="product_type" opts={options.types} />
                        <div className="mb-6">
                            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">Offers</h4>
                            <button
                                onClick={() => setFilter("limited_time", filters.limited_time === "true" ? "" : "true")}
                                data-testid="filter-limited-time"
                                className={`text-xs px-3 py-1.5 border transition-colors ${filters.limited_time === "true" ? "bg-secondary text-secondary-foreground border-secondary" : "bg-background border-border hover:border-primary"}`}
                            >
                                Limited-Time Offer
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Grid */}
                <div>
                    <div className="mb-6">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by product name, brand, or category"
                            data-testid="catalog-search"
                            className="w-full border border-border bg-transparent px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                        <p className="text-sm text-muted-foreground mt-3">Browse wholesale products and add items to your order request. No online payment required.</p>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-sm text-muted-foreground" data-testid="results-count">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</p>
                        <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden flex items-center gap-2 text-sm border border-border px-4 py-2" data-testid="toggle-filters">
                            <SlidersHorizontal size={15} /> Filters {activeCount > 0 && `(${activeCount})`}
                        </button>
                    </div>
                    {loading ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />)}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-24 border border-dashed border-border">
                            <p className="text-muted-foreground mb-4">No products match your filters.</p>
                            <button onClick={clearAll} className="text-primary font-semibold">Clear filters</button>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                            {filtered.map((p) => <ProductCard key={p.id} product={p} settings={settings} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
